// apps/web/src/offline/OfflineQuizAttemptService.js

const STORAGE_KEY =
  'offline_quiz_attempts'

// ======================================================
// GERAR ID LOCAL
// ======================================================

const createLocalId =
  () => {
    return `offline-quiz-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`
  }

// ======================================================
// LER TENTATIVAS
// ======================================================

const getPendingAttempts =
  async () => {
    try {
      const raw =
        localStorage.getItem(
          STORAGE_KEY,
        )

      if (!raw) {
        return []
      }

      const parsed =
        JSON.parse(
          raw,
        )

      return Array.isArray(
        parsed,
      )
        ? parsed
        : []
    } catch (error) {
      console.error(
        '[OFFLINE QUIZ] Erro ao ler tentativas:',
        error,
      )

      return []
    }
  }

// ======================================================
// GRAVAR TENTATIVAS
// ======================================================

const savePendingAttempts =
  async (
    attempts,
  ) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        Array.isArray(
          attempts,
        )
          ? attempts
          : [],
      ),
    )
  }

// ======================================================
// CRIAR TENTATIVA OFFLINE
// ======================================================

const createAttempt =
  async ({
    gameId,
    leadId,
    participant,
  }) => {
    if (!gameId) {
      throw new Error(
        'gameId não informado.',
      )
    }

    const id =
      createLocalId()

    const now =
      new Date()
        .toISOString()

    const record = {
      id,

      localId:
        id,

      gameId,

      leadId:
        leadId ||
        null,

      participant:
        participant ||
        null,

      answers:
        [],

      totalScore:
        0,

      answeredQuestions:
        0,

      resultRange:
        null,

      status:
        'IN_PROGRESS',

      startedAt:
        now,

      finishedAt:
        null,

      syncStatus:
        'PENDING',

      createdAt:
        now,

      updatedAt:
        now,
    }

    const current =
      await getPendingAttempts()

    await savePendingAttempts([
      ...current,
      record,
    ])

    console.log(
      '[OFFLINE QUIZ] Tentativa criada:',
      id,
    )

    return record
  }

  // ======================================================
// ADOTAR TENTATIVA ONLINE PARA CONTINUAR OFFLINE
// ======================================================

const adoptOnlineAttempt =
  async ({
    serverAttemptId,
    gameId,
    leadId,
    participant,
    answers,
    totalScore,
    answeredQuestions,
    startedAt,
  }) => {
    if (!serverAttemptId) {
      throw new Error(
        'serverAttemptId não informado.',
      )
    }

    if (!gameId) {
      throw new Error(
        'gameId não informado.',
      )
    }

    const current =
      await getPendingAttempts()

    /*
     * Evita criar mais de um espelho local
     * para a mesma tentativa do servidor.
     */
    const existing =
      current.find(
        (
          item,
        ) =>
          String(
            item.serverAttemptId ||
            '',
          ) ===
          String(
            serverAttemptId,
          ),
      )

    if (existing) {
      return existing
    }

    const id =
      createLocalId()

    const now =
      new Date()
        .toISOString()

    const normalizedAnswers =
      Array.isArray(
        answers,
      )
        ? answers
        : []

    const calculatedScore =
      normalizedAnswers.reduce(
        (
          total,
          answer,
        ) =>
          total +
          Number(
            answer?.points ||
            0,
          ),
        0,
      )

    const record = {
      id,

      localId:
        id,

      /*
       * Esta tentativa já existe na API.
       *
       * Na sincronização NÃO devemos
       * criar outra tentativa.
       */
      serverAttemptId:
        String(
          serverAttemptId,
        ),

      origin:
        'ONLINE',

      gameId,

      leadId:
        leadId ||
        null,

      participant:
        participant ||
        null,

      answers:
        normalizedAnswers,

      totalScore:
        Number.isFinite(
          Number(
            totalScore,
          ),
        )
          ? Number(
            totalScore,
          )
          : calculatedScore,

      answeredQuestions:
        Number.isFinite(
          Number(
            answeredQuestions,
          ),
        )
          ? Number(
            answeredQuestions,
          )
          : normalizedAnswers.length,

      resultRange:
        null,

      status:
        'IN_PROGRESS',

      startedAt:
        startedAt ||
        now,

      finishedAt:
        null,

      syncStatus:
        'PENDING',

      createdAt:
        now,

      updatedAt:
        now,
    }

    await savePendingAttempts([
      ...current,
      record,
    ])

    console.log(
      '[OFFLINE QUIZ] Tentativa online adotada localmente:',
      {
        localId:
          id,

        serverAttemptId:
          String(
            serverAttemptId,
          ),

        answeredQuestions:
          record
            .answeredQuestions,

        totalScore:
          record
            .totalScore,
      },
    )

    return record
  }

// ======================================================
// BUSCAR TENTATIVA
// ======================================================

const getAttemptById =
  async (
    id,
  ) => {
    if (!id) {
      return null
    }

    const attempts =
      await getPendingAttempts()

    return (
      attempts.find(
        (
          item,
        ) =>
          item.id === id ||
          item.localId === id,
      ) ||
      null
    )
  }

// ======================================================
// ATUALIZAR TENTATIVA
// ======================================================

const updateAttempt =
  async (
    attemptId,
    changes,
  ) => {
    const attempts =
      await getPendingAttempts()

    let updated =
      null

    const next =
      attempts.map(
        (
          item,
        ) => {
          if (
            item.id !== attemptId &&
            item.localId !== attemptId
          ) {
            return item
          }

          updated = {
            ...item,
            ...changes,

            updatedAt:
              new Date()
                .toISOString(),
          }

          return updated
        },
      )

    if (!updated) {
      throw new Error(
        'Tentativa offline não encontrada.',
      )
    }

    await savePendingAttempts(
      next,
    )

    return updated
  }

// ======================================================
// SALVAR RESPOSTA
// ======================================================

const saveAnswer =
  async ({
    attemptId,
    questionId,
    optionId,
    points,
  }) => {
    const attempt =
      await getAttemptById(
        attemptId,
      )

    if (!attempt) {
      throw new Error(
        'Tentativa offline não encontrada.',
      )
    }

    const answers =
      Array.isArray(
        attempt.answers,
      )
        ? [...attempt.answers]
        : []

    /*
     * Impede resposta duplicada para
     * a mesma pergunta.
     */
    const existingIndex =
      answers.findIndex(
        (
          answer,
        ) =>
          answer.questionId ===
          questionId,
      )

    const answer = {
      questionId,

      optionId,

      points:
        Number(
          points ||
          0,
        ),

      answeredAt:
        new Date()
          .toISOString(),
    }

    if (
      existingIndex >= 0
    ) {
      answers[
        existingIndex
      ] = answer
    } else {
      answers.push(
        answer,
      )
    }

    const totalScore =
      answers.reduce(
        (
          total,
          item,
        ) =>
          total +
          Number(
            item.points ||
            0,
          ),
        0,
      )

    return updateAttempt(
      attemptId,
      {
        answers,

        totalScore,

        answeredQuestions:
          answers.length,
      },
    )
  }

// ======================================================
// FINALIZAR TENTATIVA
// ======================================================

const finishAttempt =
  async ({
    attemptId,
    resultRange,
  }) => {
    const attempt =
      await getAttemptById(
        attemptId,
      )

    if (!attempt) {
      throw new Error(
        'Tentativa offline não encontrada.',
      )
    }

    return updateAttempt(
      attemptId,
      {
        status:
          'COMPLETED',

        resultRange:
          resultRange ||
          null,

        finishedAt:
          new Date()
            .toISOString(),

        syncStatus:
          'PENDING',
      },
    )
  }

// ======================================================
// REMOVER DEPOIS DA SINCRONIZAÇÃO
// ======================================================

const removeAttempt =
  async (
    id,
  ) => {
    if (!id) {
      return
    }

    const attempts =
      await getPendingAttempts()

    const next =
      attempts.filter(
        (
          item,
        ) =>
          item.id !== id &&
          item.localId !== id,
      )

    await savePendingAttempts(
      next,
    )
  }

// ======================================================
// EXPORT
// ======================================================

const OfflineQuizAttemptService = {
  createAttempt,
  adoptOnlineAttempt,
  getAttemptById,
  getPendingAttempts,
  saveAnswer,
  finishAttempt,
  updateAttempt,
  removeAttempt,
}

export default OfflineQuizAttemptService